import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Pagination,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  LocationOn,
  Refresh,
  CellTower,
  Map,
  AccountTree,
  CheckCircle,
  Cancel,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import odcService from '../services/odcService';
import spopService from '../services/spopService';
import { useAuth } from '../hooks/useAuth';
import { ODC, SPOP, ODCStats } from '../types';

const ODCs: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [odcs, setODCs] = useState<ODC[]>([]);
  const [spops, setSPOPs] = useState<SPOP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [spopFilter, setSPOPFilter] = useState<string>('all');
  const [stats, setStats] = useState<ODCStats>({
    total: 0,
    active: 0,
    inactive: 0,
    bySpop: 0,
    averageRadius: 0,
  });

  // Get SPOP filter for operators
  const operatorSpopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;

  useEffect(() => {
    fetchODCs();
    fetchSPOPs();
    fetchStats();
  }, [page, search, statusFilter, spopFilter]);

  const fetchODCs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page,
        limit: 10,
        search: search || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'true',
        spopId: operatorSpopFilter || (spopFilter === 'all' ? undefined : spopFilter), // Use operator's SPOP or selected filter
      };
      console.log('Fetching ODCs with params:', params);
      const response = await odcService.getODCs(params);
      console.log('ODC Response:', response);
      setODCs(response.data?.odcs || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (err: any) {
      console.error('Error fetching ODCs:', err);
      setError(err.response?.data?.message || 'Gagal memuat data ODC');
    } finally {
      setLoading(false);
    }
  };

  const fetchSPOPs = async () => {
    try {
      // For operators, apply SPOP filtering to only show their SPOP
      const filters = operatorSpopFilter ? { spop_id: operatorSpopFilter } : undefined;
      const response = await spopService.getSPOPs(1, 100, undefined, undefined, filters);
      if (response.data?.success) {
        setSPOPs(response.data.data.spops);
        
        // For operators, automatically set the SPOP filter to their assigned SPOP
        if (operatorSpopFilter && response.data.data.spops.length > 0) {
          setSPOPFilter(operatorSpopFilter);
        }
      }
    } catch (err) {
      console.error('Error fetching SPOPs:', err);
    }
  };

  const fetchStats = async () => {
    try {
      // For operators, apply SPOP filtering to stats
      const filters = operatorSpopFilter ? { spop_id: operatorSpopFilter } : undefined;
      const response = await odcService.getODCStats(filters);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus ODC ini?')) {
      try {
        await odcService.deleteODC(id);
        fetchODCs();
        fetchStats();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Gagal menghapus ODC');
      }
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const openInMaps = (latitude: number, longitude: number) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  const getSPOPName = (spopId: string) => {
    const spop = spops.find(s => s.id === spopId);
    return spop ? spop.name : 'Unknown SPOP';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Data ODC
          </Typography>
          {user?.role === 'operator' && user?.spop && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
              SPOP: {user.spop.name} ({user.spop.areaCode})
            </Typography>
          )}
        </Box>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/master-data/odc/create')}
          >
            Tambah ODC Baru
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ mb: 3 }}>
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <AccountTree color="primary" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total ODC
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircle color="success" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ODC Aktif
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Cancel color="error" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.inactive}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ODC Tidak Aktif
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <TrendingUp color="info" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.averageRadius ? Math.round(stats.averageRadius) : 0}m
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rata-rata Radius
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Cari ODC..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">Semua Status</MenuItem>
                <MenuItem value="true">Aktif</MenuItem>
                <MenuItem value="false">Tidak Aktif</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>SPOP</InputLabel>
              <Select
                value={spopFilter}
                onChange={(e) => setSPOPFilter(e.target.value)}
                label="SPOP"
                disabled={user?.role === 'operator'} // Disable for operators
              >
                <MenuItem value="all">Semua SPOP</MenuItem>
                {spops.map((spop) => (
                  <MenuItem key={spop.id} value={spop.id}>
                    {spop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setSPOPFilter('all');
                setPage(1);
              }}
            >
              Reset
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ODC Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Full ODC Code</TableCell>
                      <TableCell>SPOP</TableCell>
                      <TableCell>Alamat</TableCell>
                      <TableCell>Radius</TableCell>
                      <TableCell>Lokasi</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {odcs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            Tidak ada data ODC
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      odcs.map((odc) => (
                        <TableRow key={odc.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {odc.fullCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<CellTower />}
                              label={getSPOPName(odc.spopId)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {odc.address}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {typeof odc.radius === 'number' ? odc.radius.toLocaleString() : parseInt(odc.radius || '0').toLocaleString()} m
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <Typography variant="caption" fontFamily="monospace">
                                {typeof odc.latitude === 'number' ? odc.latitude.toFixed(6) : parseFloat(odc.latitude || '0').toFixed(6)}, {typeof odc.longitude === 'number' ? odc.longitude.toFixed(6) : parseFloat(odc.longitude || '0').toFixed(6)}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<Map />}
                                onClick={() => openInMaps(
                                  typeof odc.latitude === 'number' ? odc.latitude : parseFloat(odc.latitude || '0'),
                                  typeof odc.longitude === 'number' ? odc.longitude : parseFloat(odc.longitude || '0')
                                )}
                              >
                                Lihat di Maps
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={odc.isActive ? 'Aktif' : 'Tidak Aktif'}
                              color={odc.isActive ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              {user?.role === 'admin' && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/master-data/odc/edit/${odc.id}`)}
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(odc.id)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </>
                              )}
                              {user?.role === 'operator' && (
                                <Typography variant="body2" color="text.secondary">
                                  Hanya dapat melihat
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ODCs;
